import { cn } from '@/lib/utils';
import { AgentAvatar } from '../agent/AgentAvatar';
import { Button } from '../ui/button';
import { Plus, Sparkles, Mic } from 'lucide-react';
import { motion } from 'framer-motion';

interface FeedEmptyProps {
  className?: string;
  onStartSOP?: () => void;
  onAskAgent?: () => void;
}

export function FeedEmpty({ className, onStartSOP, onAskAgent }: FeedEmptyProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={cn(
        'flex flex-col items-center justify-center text-center py-16 px-8',
        className
      )}
    >
      {/* Agent avatar with celebration */}
      <motion.div 
        className="relative mb-8"
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
      >
        <AgentAvatar size="lg" />
        <motion.div 
          className="absolute -top-2 -right-2 text-2xl"
          animate={{ rotate: [0, 15, -15, 0] }}
          transition={{ repeat: Infinity, duration: 2, delay: 1 }}
        >
          🎉
        </motion.div>
      </motion.div>

      {/* Message - Ogilvy-level copy */}
      <h3 className="text-2xl font-bold text-foreground mb-3">
        All caught up.
      </h3>
      <p className="text-muted-foreground max-w-xs mb-10 text-base leading-relaxed">
        I'll ping you when something needs attention. Or start something new.
      </p>

      {/* Actions - large touch targets */}
      <div className="flex flex-col gap-4 w-full max-w-sm">
        <Button
          variant="hero"
          className="h-14 text-base gap-3 touch-target"
          onClick={onStartSOP}
        >
          <Plus className="h-5 w-5" />
          Start a Run
        </Button>
        <Button
          variant="outline"
          className="h-14 text-base gap-3 touch-target"
          onClick={onAskAgent}
        >
          <Sparkles className="h-5 w-5" />
          Ask Ritual
        </Button>
      </div>

      {/* Voice hint */}
      <motion.div 
        className="mt-10 flex items-center gap-3 text-muted-foreground"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
      >
        <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
          <Mic className="h-4 w-4" />
        </div>
        <span className="text-sm">Or tap the mic and tell me what you need</span>
      </motion.div>
    </motion.div>
  );
}
